import { readNullableOneOf, readNullableString, readNumber, readOneOf, readRecord, readRecords, readString, readStringList } from '@/lib/readers';
import type { LiveRow } from '@/lib/pendingWrites';

export const BRAND_STATES = ['draft', 'ready'] as const;
export type BrandState = (typeof BRAND_STATES)[number];

/** The four mandatory materials, in the order the readiness guide lists them. */
export const MATERIALS = ['logos', 'colours', 'font', 'guidelines'] as const;
export type Material = (typeof MATERIALS)[number];

export type BrandChecklist = Record<Material, boolean>;

/** One palette entry, as CMYK percentages. */
export interface BrandColour {
  label: string;
  cyan: number;
  magenta: number;
  yellow: number;
  key: number;
}

export const CMYK_CHANNELS = ['cyan', 'magenta', 'yellow', 'key'] as const;

/** Returns true if the two colours have the same CMYK channels. */
export function sameShade(left: BrandColour, right: BrandColour): boolean {
  return CMYK_CHANNELS.every((channel) => left[channel] === right[channel]);
}

/** Returns the id of one shade: its four channels, joined. */
export function shadeIdOf(colour: Pick<BrandColour, (typeof CMYK_CHANNELS)[number]>): string {
  return CMYK_CHANNELS.map((channel) => colour[channel]).join('-');
}

export const FONT_SOURCES = ['upload', 'google'] as const;
export type FontSource = (typeof FONT_SOURCES)[number];

/** One font of the brand. Its file is always ours, uploaded or copied from Google Fonts. */
export interface BrandFont {
  fontId: string;
  family: string;
  objectName: string;
  source: FontSource;
}

/** Returns the id of one font. */
export function fontIdOf(font: BrandFont): string {
  return font.fontId;
}

/** What the generation service writes onto the brand while it reads the guidelines. */
export const EXTRACTION_STATUSES = ['queued', 'in_progress', 'completed', 'failed'] as const;
export type ExtractionStatus = (typeof EXTRACTION_STATUSES)[number];

export interface Brand extends LiveRow {
  id: string;
  name: string;
  state: BrandState;
  checklist: BrandChecklist;
  colours: BrandColour[];
  fonts: BrandFont[];
  tags: string[];
  /** A deactivated brand keeps its documents but leaves the hub. */
  isActive: boolean;
  extractionStatus: ExtractionStatus | null;
  /** Colours proposed from the guidelines, kept apart from the palette until added. */
  extractedColours: BrandColour[];
}

function percent(value: unknown): number {
  return Math.min(100, Math.max(0, Math.round(readNumber(value))));
}

function colours(value: unknown): BrandColour[] {
  return readRecords(value)
    .filter((item) => typeof item.label === 'string' && item.label)
    .map((item) => ({
      label: readString(item.label),
      cyan: percent(item.cyan),
      magenta: percent(item.magenta),
      yellow: percent(item.yellow),
      key: percent(item.key),
    }));
}

/** Returns every well-formed font. It skips an entry without an id, a family or a file. */
function fonts(value: unknown): BrandFont[] {
  return readRecords(value)
    .filter((item) => readNullableString(item.font_id) && readNullableString(item.family) && readNullableString(item.object_name))
    .map((item) => ({
      fontId: readString(item.font_id),
      family: readString(item.family),
      objectName: readString(item.object_name),
      source: readOneOf(FONT_SOURCES, item.source, 'upload'),
    }));
}

/** Returns one brand document, or null if the document has no name. */
export function parseBrand(id: string, raw: Record<string, unknown>): Brand | null {
  const name = readString(raw.name);
  if (!name) return null;
  const checklist = readRecord(raw.checklist);
  return {
    id,
    name,
    state: readOneOf(BRAND_STATES, raw.state, 'draft'),
    checklist: {
      logos: checklist.logos === true,
      colours: checklist.colours === true,
      font: checklist.font === true,
      guidelines: checklist.guidelines === true,
    },
    colours: colours(raw.colours),
    fonts: fonts(raw.fonts),
    tags: readStringList(raw.tags),
    isActive: raw.is_active !== false,
    extractionStatus: readNullableOneOf(EXTRACTION_STATUSES, raw.status),
    extractedColours: colours(raw.extracted_colours),
  };
}

/** Returns the number of the four necessary materials that the brand has. */
export function materialsDone(brand: Brand): number {
  return MATERIALS.filter((material) => brand.checklist[material]).length;
}

/** Returns the hex value of one CMYK colour. */
export function cmykToHex(colour: BrandColour): string {
  const channel = (value: number) =>
    Math.round(255 * (1 - value / 100) * (1 - colour.key / 100))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(colour.cyan)}${channel(colour.magenta)}${channel(colour.yellow)}`;
}

/** Returns the gradient of the brand palette, or undefined if the brand has no colours. */
export function paletteGradient(brand: Brand): string | undefined {
  if (brand.colours.length === 0) return undefined;
  const stops = brand.colours.slice(0, 4).map(cmykToHex);
  return `linear-gradient(135deg, ${(stops.length > 1 ? stops : [...stops, ...stops]).join(', ')})`;
}
